import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Check, Search, ChevronDown, X, Filter, Loader2 } from "lucide-react";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { DoneAll, SearchOff } from "@mui/icons-material";

const MultiSelectWithCheckbox = ({
  borderLeftRadius,
  options = [],
  value = [],
  onChange = () => { },
  placeholder = "Select options",
  labelKey = "selected",
  width = "280px",
  sortFn = (a, b) => a.label?.localeCompare(b.label),
  className = "",
  showCountBadge = true,
  virtualScroll = true,
  itemsPerView = 15,
  loading = false,
  sort = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [scrollTop, setScrollTop] = useState(0);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsListRef = useRef(null);

  const { sortedOptions, filteredOptions } = useMemo(() => {
    const sorted = sort ? [...options].sort(sortFn) : [...options];

    const selectedSet = new Set(value.map((v) => v.value));

    const selected = sorted.filter((opt) => selectedSet.has(opt.value));
    const unselected = sorted.filter((opt) => !selectedSet.has(opt.value));

    const allOptions = [...selected, ...unselected];

    const filtered = searchQuery
      ? allOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
      : allOptions;

    return { sortedOptions: sorted, filteredOptions: filtered };
  }, [options, sortFn, searchQuery, value, sort]);

  const totalItems = filteredOptions.length;
  const itemHeight = 44;
  const buffer = 5;

  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - buffer);
  const endIndex = Math.min(
    totalItems,
    Math.ceil((scrollTop + 400) / itemHeight) + buffer
  );

  const visibleOptions = virtualScroll
    ? filteredOptions.slice(startIndex, endIndex)
    : filteredOptions;

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const handleCheckboxChange = useCallback(
    (option, e) => {
      e.stopPropagation();
      const isSelected = value.some((v) => v.value === option.value);
      let newValue;

      if (isSelected) {
        newValue = value.filter((v) => v.value !== option.value);
      } else {
        newValue = [...value, option];
      }

      onChange(newValue);
    },
    [value, onChange]
  );

  const handleRemoveItem = useCallback(
    (optionValue, e) => {
      e.stopPropagation();
      const newValue = value.filter((v) => v.value !== optionValue);
      onChange(newValue);
    },
    [value, onChange]
  );

  const handleClearAll = useCallback(
    (e) => {
      e.stopPropagation();
      onChange([]);
    },
    [onChange]
  );

  const handleSelectAll = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(filteredOptions);
    },
    [filteredOptions, onChange]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (optionsListRef.current) {
      optionsListRef.current.scrollTop = 0;
      setScrollTop(0);
    }
  }, [searchQuery]);

  const selectedCount = value.length;
  const isAllSelected =
    filteredOptions.length > 0 && selectedCount === filteredOptions.length;

  return (
    <div
      ref={containerRef}
      className={`relative z-50 ${className}`}
      style={{ width }}
    >
      <div
        className={`
          w-full min-h-[46px] px-4 py-3 
          bg-white border ${borderLeftRadius ? 'rounded-l-full' : 'rounded-lg'} cursor-pointer 
          transition-all duration-200
          flex items-center justify-between
          ${isOpen
            ? "border-blue-500 ring-1 ring-blue-100 shadow-sm"
            : "border-gray-200 hover:border-gray-300"
          }
        `}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0">
            <Filter size={18} className="text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">
              {selectedCount > 0 ? (
                <span className="text-gray-800">
                  {selectedCount} {labelKey}
                </span>
              ) : (
                <span className="text-gray-500">{placeholder}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">


          {loading && (
            <div className="animate-spin">
              <Loader2 size={16} className="text-gray-400" />
            </div>
          )}

          <ChevronDown
            size={18}
            className={`
              text-gray-400 transition-transform duration-200
              ${isOpen ? "rotate-180" : ""}
            `}
          />
        </div>
      </div>

      {/* Dropdown  */}
      {isOpen && (
        <div className="absolute w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        style={{ zIndex: 999 }}
        >
          {/*  Search */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-white text-gray-900 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 block"
                style={{ color: "#111827" }}
                onClick={(e) => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery("");
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600"
                >
                  <X size={14} className="text-gray-400" />
                </button>
              )}
            </div>
          </div>

          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!isAllSelected && (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 border border-blue-100 transition-all flex items-center justify-center"
                    title="Select All"
                  >
                    <DoneAll sx={{ fontSize: 20 }} />
                  </button>
                )}

                {/* Clear All Button */}
                {selectedCount > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="p-1.5 rounded-md text-red-500 hover:bg-red-50 border border-red-100 transition-all flex items-center justify-center"
                    title="Clear All"
                  >
                    <SearchOff sx={{ fontSize: 20 }} />
                  </button>
                )}
              </div>

              {/* count display */}
              <div className="text-xs text-gray-500">
                {selectedCount > 0 ? (
                  <span className="font-medium text-gray-700">
                    {selectedCount}
                  </span>
                ) : (
                  "0"
                )}{" "}
                selected
              </div>
            </div>
          </div>

          <div
            ref={optionsListRef}
            onScroll={handleScroll}
            className="max-h-64 overflow-y-auto scrollbar-thin"
            style={{
              height: Math.min(totalItems * itemHeight, 64 * 16) + "px",
              position: "relative",
            }}
          >
            {loading && filteredOptions.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin">
                  <Loader2 size={20} className="text-gray-400" />
                </div>
              </div>
            ) : filteredOptions.length > 0 ? (
              <>
                {virtualScroll && (
                  <div style={{ height: `${startIndex * itemHeight}px` }} />
                )}

                {/*  items */}
                {visibleOptions.map((option) => {
                  const isSelected = value.some(
                    (v) => v.value === option.value
                  );
                  return (
                    <div
                      key={option.value}
                      style={virtualScroll ? { height: `${itemHeight}px` } : {}}
                      className={`
                        px-3 flex items-center cursor-pointer border-b border-gray-100 last:border-b-0
                        transition-colors duration-150
                        ${isSelected
                          ? "bg-blue-50 border-l-4 border-l-blue-500"
                          : "hover:bg-gray-50"
                        }
                      `}
                      onClick={(e) => handleCheckboxChange(option, e)}
                    >
                      <div className="flex items-center gap-3 w-full py-2">
                        {/* Checkbox */}
                        <div
                          className={`
                          w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                          transition-all duration-150
                          ${isSelected
                              ? "border-blue-500 bg-blue-500"
                              : "border-gray-400 hover:border-blue-400"
                            }
                        `}
                        >
                          {isSelected && (
                            <Check size={12} className="text-white" />
                          )}
                        </div>

                        <span
                          title={option.label}
                          className={`
                          text-sm truncate flex-1
                          ${isSelected
                             ? "text-blue-700 font-medium"
                             : "text-gray-700"
                           }
                        `}
                        >
                          {option.label}
                          {isSelected && (
                            <span className="ml-2 text-xs text-blue-500 font-normal">
                              ✓
                            </span>
                          )}
                        </span>

                        {isSelected && (
                          <button
                            type="button"
                            onClick={(e) => handleRemoveItem(option.value, e)}
                            className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                            title="Remove"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {virtualScroll && (
                  <div
                    style={{
                      height: `${Math.max(
                        0,
                        (totalItems - endIndex) * itemHeight
                      )}px`,
                    }}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Search size={18} className="text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm font-medium">
                  No options found
                </p>
                {searchQuery && (
                  <p className="text-gray-400 text-xs mt-1">
                    No results for "{searchQuery}"
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #f8f9fa;
          border-radius: 2px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default React.memo(MultiSelectWithCheckbox);
